import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Polyline, useMap, useMapEvents } from "react-leaflet";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import useMapElementStyles from "../useMapElementStyles";
import ToolButton from "../tools/ToolButton";
import { findBiggestName } from "../helperFunctions/findBiggestName";

const EdgeTool = ({
  onAlert,
  mapElements,
  selectedElement,
  active,
  onClick,
  setMapElements,
  open,
  defaultProps,
}) => {
  const { t } = useTranslation();
  const [edgeNodes, setEdgeNodes] = useState(null);
  // "gravity" draws a normal edge, "pumped" draws a pressure pipe
  const [edgeType, setEdgeType] = useState("gravity");
  const map = useMap(); // Access the Leaflet map instance

  const mapStyles = useMapElementStyles();

  useEffect(() => {
    if (active && selectedElement.type === "node")
      handleNodeClick(selectedElement);
  }, [selectedElement]);

  useEffect(() => {
    setEdgeNodes(null);
  }, [active]);

  function handleNodeClick(node) {
    if (!edgeNodes) {
      // set startnode
      setEdgeNodes({ start: node, end: node });
    } else if (edgeNodes && edgeNodes.start.id === node.id) {
      onAlert("An edge requires two different nodes.", "warning");
    } else if (!edgeExisting(edgeNodes.start.id, node.id)) {
      createNewEdge(edgeNodes.start, node);
    }
  }

  function edgeExisting(nodeId1, nodeId2) {
    // Check if there is already an edge between the nodes
    const hasConstraints = mapElements.some(
      (mapElement) =>
        (mapElement.type === "edge" &&
          mapElement.startNodeId === nodeId1 &&
          mapElement.endNodeId === nodeId2) ||
        (mapElement.startNodeId === nodeId2 && mapElement.endNodeId === nodeId1)
    );
    if (hasConstraints) {
      onAlert("An identical edge already exists.", "warning");
    }

    return hasConstraints;
  }

  function createNewEdge(node1, node2) {
    // create new edge
    const startNode = node1;
    const endNode = node2;
    // clone the default props and set the pressure-pipe flag from the selected
    // sub-mode (gravity -> false, pumped -> true); cloning also avoids sharing
    // the same props array reference across all drawn edges
    const props = (defaultProps || []).map((prop) =>
      prop.key === "pressure_pipe"
        ? { ...prop, value: edgeType === "pumped" }
        : { ...prop }
    );
    const newEdge = {
      startNodeId: startNode.id,
      endNodeId: endNode.id,
      isSaved: false,
      id: uuidv4(),
      type: "edge",
      name: findBiggestName(mapElements) + 1,
      props: props,
      calc: [
        {
          key: "diameter",
          label: "diameter",
          type: "number",
          unit: "m",
          value: null,
        },
        {
          key: "outlet_offset",
          label: "outlet_offset",
          type: "number",
          unit: "m",
          value: null,
        },
        {
          key: "first_year_pipe_flow",
          label: "first_year_pipe_flow",
          type: "number",
          unit: "l/s",
          value: null,
        },
        {
          key: "final_year_pipe_flow",
          label: "final_year_pipe_flow",
          type: "number",
          unit: "l/s",
          value: null,
        },
      ],
    };

    setMapElements([...mapElements, newEdge]);
    // new Edge from last node
    setEdgeNodes({ start: endNode, end: endNode });
  }

  useMapEvents({
    contextmenu: (e) => {
      // Prevent the default context menu behavior
      e.originalEvent.preventDefault();
      setEdgeNodes(null);
    },
    mousemove: active ? handleMouseMove : () => {},
  });

  function handleMouseMove(e) {
    if (!edgeNodes || !edgeNodes.start) return;

    const { lat, lng } = e.latlng;

    // Convert start and end positions to container points
    const startContainerPoint = map.latLngToContainerPoint({
      lat: edgeNodes.start.position[0],
      lng: edgeNodes.start.position[1],
    });
    const endContainerPoint = map.latLngToContainerPoint({ lat, lng });

    // Calculate the distance in pixels
    const originalDistance = startContainerPoint.distanceTo(endContainerPoint);

    // Adjust the distance based on the desired pixel difference (5 pixels in this case)
    const adjustedDistance = originalDistance - 3;

    // Calculate the unit vector between start and end points
    const directionX =
      (endContainerPoint.x - startContainerPoint.x) / originalDistance;
    const directionY =
      (endContainerPoint.y - startContainerPoint.y) / originalDistance;

    // Calculate the new end position in container points
    const newEndContainerPoint = {
      x: startContainerPoint.x + directionX * adjustedDistance,
      y: startContainerPoint.y + directionY * adjustedDistance,
    };

    // Convert the new end position back to latLng
    const newEndLatLng = map.containerPointToLatLng(newEndContainerPoint);

    setEdgeNodes({
      ...edgeNodes,
      end: { ...edgeNodes.end, position: [newEndLatLng.lat, newEndLatLng.lng] },
    });
  }

  return (
    <>
      <Box className="relative flex flex-col items-center justify-center">
        {/* Sub-controls appear above Edge once the tool is active:
            Gravity (normal edge, left) and Pumped (pressure pipe, right). */}
        {active && (
          <Box
            className="absolute flex flex-row items-end gap-8"
            sx={{
              bottom: "100%",
              mb: 1,
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <ToolButton
              src={"/assets/svg/tools/edge-gravity.svg"}
              label={t("Gravity")}
              onClick={() => setEdgeType("gravity")}
              active={edgeType === "gravity"}
              open={open}
              raiseOnActive={false}
            />
            <ToolButton
              src={"/assets/svg/tools/edge-pumped.svg"}
              label={t("Pumped")}
              onClick={() => setEdgeType("pumped")}
              active={edgeType === "pumped"}
              open={open}
              raiseOnActive={false}
            />
          </Box>
        )}
        <ToolButton
          src={"/assets/svg/tools/edge.svg"}
          label="Edge"
          onClick={(e) => onClick(e, "edgeTool")}
          active={active}
          open={open}
        />
      </Box>

      {edgeNodes && (
        <Polyline
          key={edgeNodes}
          weight={mapStyles.edge.size.preview}
          positions={[edgeNodes.start.position, edgeNodes.end.position]}
          color={mapStyles.edge.colors.preview}
        />
      )}
    </>
  );
};

export default EdgeTool;
